import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Copy, Phone, MessageCircle, Clock, BookOpen, School, Baby } from "lucide-react";
import { toast } from "sonner";
import { LeadBadge } from "@/components/admin/LeadBadge";
import { useConfig } from "@/context/ConfigContext";

export const buildWhatsAppMessage = (lead, institute) =>
  `Hello ${lead.parent_name} ji, greetings from ${institute}! 😊\n\n` +
  `Thank you so much for your interest in ${lead.course ? `our ${lead.course} programme` : "our preschool"}. ` +
  (lead.timing ? `We have noted your preferred timing (${lead.timing}).` : "") + `\n\n` +
  `We would be very happy to share all the details and invite you and your little one for a school visit. ` +
  `Kindly let us know a convenient time to connect.\n\n` +
  `Warm regards,\nTeam ${institute}`;

const Row = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 mt-0.5 text-brand" />
    <div>
      <p className="text-xs text-stone-500">{label}</p>
      <p className="text-sm font-medium" data-testid={testid}>{value}</p>
    </div>
  </div>
);

export default function LeadDrawer({ lead, onClose }) {
  const { config } = useConfig();
  if (!lead) return null;
  const message = buildWhatsAppMessage(lead, lead.school_name || config?.institute_name || "");
  const copy = async () => {
    await navigator.clipboard.writeText(message);
    toast.success("WhatsApp message copied");
  };

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="lead-detail-drawer">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2"><LeadBadge label={lead.label} /></div>
          <SheetTitle className="text-2xl font-display" data-testid="lead-detail-name">{lead.parent_name}</SheetTitle>
          <SheetDescription>Enquiry received {new Date(lead.created_at).toLocaleString("en-IN")}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 grid grid-cols-1 gap-4">
          <Row icon={Phone} label="Phone" value={lead.phone} testid="lead-detail-phone" />
          <Row icon={BookOpen} label="Interested course" value={lead.course || "—"} testid="lead-detail-course" />
          <Row icon={Clock} label="Preferred timing" value={lead.timing || "—"} testid="lead-detail-timing" />
          {lead.school_name && <Row icon={School} label="School" value={lead.school_name} testid="lead-detail-school" />}
          {lead.child_age && <Row icon={Baby} label="Child's age" value={lead.child_age} testid="lead-detail-child-age" />}
          {lead.message && <Row icon={MessageCircle} label="Parent's message" value={lead.message} testid="lead-detail-message" />}
          <Row icon={BookOpen} label="Source" value={lead.source === "form" ? "Enquiry form" : "Chat assistant"} testid="lead-detail-source" />
        </div>

        <div className="mt-6 rounded-xl bg-brand-soft p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand mb-2">Summary</p>
          <p className="text-sm leading-relaxed text-stone-700" data-testid="lead-detail-summary">{lead.summary}</p>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 flex items-center gap-1.5">
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp follow-up
            </p>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm bg-[#E7FCE3] border border-green-200 rounded-xl p-4 text-stone-800"
            data-testid="whatsapp-message-text">{message}</pre>
          <div className="mt-3 flex gap-2">
            <button onClick={copy} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full btn-brand text-sm font-semibold" data-testid="copy-whatsapp-button">
              <Copy className="w-4 h-4" /> Copy message
            </button>
            <a href={`https://wa.me/91${lead.phone}?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors" data-testid="open-whatsapp-link">
              Open WhatsApp
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
